import { useState } from 'react';
import { User, OperationLog } from '../types';
import { mockOperationLogs } from '../mockData';

interface UserDetailModalProps {
  user: User;
  onClose: () => void;
}

export default function UserDetailModal({ user, onClose }: UserDetailModalProps) {
  const [activeTab, setActiveTab] = useState<'info' | 'logs'>('info');

  const maskIdNumber = (idNumber: string) => {
    return idNumber.replace(/^(.{6})(.*)(.{4})$/, '$1***$3');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">用户详情</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="border-b border-gray-200">
          <div className="flex gap-8 px-6">
            <button
              onClick={() => setActiveTab('info')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'info'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              基本信息
            </button>
            <button
              onClick={() => setActiveTab('logs')}
              className={`py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === 'logs'
                  ? 'border-teal-600 text-teal-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              操作日志
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'info' ? (
            <div className="space-y-6">
              {/* 基本信息 */}
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-4">基本信息</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <img src={user.avatar} alt="" className="w-16 h-16 rounded-full object-cover" />
                    <div>
                      <div className="text-sm text-gray-500 mb-1">头像</div>
                      <div className="flex flex-wrap gap-1">
                        {user.userType.map((type) => (
                          <span key={type} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800">
                            {type}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">UID</div>
                    <div className="text-sm text-gray-900">{user.uid}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">用户名</div>
                    <div className="text-sm text-gray-900">{user.username}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">手机号码</div>
                    <div className="text-sm text-gray-900">{user.phone}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">微信</div>
                    <div className="text-sm text-gray-900">{user.wechat || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">QQ</div>
                    <div className="text-sm text-gray-900">{user.qq || '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">注册时间</div>
                    <div className="text-sm text-gray-900">{user.registerTime}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">最近登录时间</div>
                    <div className="text-sm text-gray-900">{user.lastLoginTime}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">实名状态</div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.isVerified ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {user.isVerified ? '已实名' : '未实名'}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">账号状态</div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.status === 'normal' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {user.status === 'normal' ? '正常' : '禁用'}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">余额</div>
                    <div className="text-sm text-gray-900">¥{user.balance.toFixed(2)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">积分</div>
                    <div className="text-sm text-gray-900">{user.points}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">租户</div>
                    <div className="text-sm text-gray-900">{user.tenants.length > 0 ? user.tenants.join(', ') : '-'}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-500 mb-1">备注</div>
                    <div className="text-sm text-gray-900">{user.remark || '-'}</div>
                  </div>
                </div>
              </div>

              {/* 认证信息 */}
              {user.personalInfo && (
                <div className="pt-6 border-t border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900 mb-4">个人信息</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">姓名</div>
                      <div className="text-sm text-gray-900">{user.personalInfo.name}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">证件类型</div>
                      <div className="text-sm text-gray-900">{user.personalInfo.idType}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">身份证号</div>
                      <div className="text-sm text-gray-900">{maskIdNumber(user.personalInfo.idNumber)}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">认证时间</div>
                      <div className="text-sm text-gray-900">{user.personalInfo.verifyTime}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* 校园信息 */}
              {user.campusInfo && (
                <div className="pt-6 border-t border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900 mb-4">校园信息</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">学号/职工号</div>
                      <div className="text-sm text-gray-900">{user.campusInfo.studentId}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">机构代码</div>
                      <div className="text-sm text-gray-900">{user.campusInfo.orgCode}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">机构中文名称</div>
                      <div className="text-sm text-gray-900">{user.campusInfo.orgName}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">电子邮箱</div>
                      <div className="text-sm text-gray-900">{user.campusInfo.email}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">年级</div>
                      <div className="text-sm text-gray-900">{user.campusInfo.grade}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">班级代码</div>
                      <div className="text-sm text-gray-900">{user.campusInfo.classCode}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">班级中文名称</div>
                      <div className="text-sm text-gray-900">{user.campusInfo.className}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">专业代码</div>
                      <div className="text-sm text-gray-900">{user.campusInfo.majorCode}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">专业中文名称</div>
                      <div className="text-sm text-gray-900">{user.campusInfo.majorName}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">学籍状态</div>
                      <div className="text-sm text-gray-900">{user.campusInfo.enrollmentStatus}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">学制</div>
                      <div className="text-sm text-gray-900">{user.campusInfo.schoolSystem}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">入学时间</div>
                      <div className="text-sm text-gray-900">{user.campusInfo.enrollmentTime}</div>
                    </div>
                    {user.campusInfo.employeeStatus && (
                      <div>
                        <div className="text-sm text-gray-500 mb-1">职工状态</div>
                        <div className="text-sm text-gray-900">{user.campusInfo.employeeStatus}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* 企业信息 */}
              {user.enterpriseInfo && (
                <div className="pt-6 border-t border-gray-200">
                  <h3 className="text-base font-semibold text-gray-900 mb-4">企业信息</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-500 mb-1">企业名称</div>
                      <div className="text-sm text-gray-900">{user.enterpriseInfo.name}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">信用代码</div>
                      <div className="text-sm text-gray-900">{user.enterpriseInfo.creditCode}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-500 mb-1">加入时间</div>
                      <div className="text-sm text-gray-900">{user.enterpriseInfo.joinTime}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <h3 className="text-base font-semibold text-gray-900 mb-4">操作日志</h3>
              <div className="space-y-3">
                {mockOperationLogs.map((log, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800">
                          {log.type}
                        </span>
                        <span className="text-sm text-gray-900">{log.detail}</span>
                      </div>
                      <span className="text-xs text-gray-500">{log.time}</span>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-600">
                      <span>IP: {log.ip}</span>
                      <span>设备: {log.device}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
